import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AuthContext } from "../state/AuthContext";
import SpeechInput from "../components/SpeechInput";
import PaymentReceiptModal from "../components/PaymentReceiptModal";

export default function PendingBalancesPage() {
  const { http } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({
    totalCustomersWithBalance: 0,
    totalFees: 0,
    totalCollected: 0,
    totalOutstanding: 0,
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEnquiryForPayment, setSelectedEnquiryForPayment] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await http.get("/api/reports/pending-balances");
      setItems(res.data.items || []);
      if (res.data.summary) {
        setSummary(res.data.summary);
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load pending balance list.");
    } finally {
      setLoading(false);
    }
  }, [http]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredItems = useMemo(() => {
    if (!searchTerm.trim()) return items;
    const query = searchTerm.trim().toLowerCase();
    return items.filter((item) => {
      const appNo = String(item.applicationNo || "").toLowerCase();
      const name = String(item.customerName || "").toLowerCase();
      const mobile = String(item.customerMobile || "").toLowerCase();
      const workType = String(item.workTypeName || "").toLowerCase();
      const workDesc = String(item.workDescription || item.workName || "").toLowerCase();
      const vehicle = String(item.vehicleNo || "").toLowerCase();

      return (
        appNo.includes(query) ||
        name.includes(query) ||
        mobile.includes(query) ||
        workType.includes(query) ||
        workDesc.includes(query) ||
        vehicle.includes(query)
      );
    });
  }, [items, searchTerm]);

  return (
    <div className="d-grid gap-3">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <h4 className="mb-1">Customer Pending Balances 💰</h4>
          <p className="text-muted mb-0">
            View all customers with outstanding payment balances and collect payments with instant receipt generation.
          </p>
        </div>
        <button className="btn btn-outline-secondary btn-sm" onClick={loadData}>
          Refresh List
        </button>
      </div>

      {error ? <div className="alert alert-danger">{error}</div> : null}

      <div className="row g-3">
        <div className="col-12 col-md-3">
          <div className="card shadow-sm border-start border-warning border-4">
            <div className="card-body">
              <div className="text-muted small">Pending Customers</div>
              <div className="fs-3 fw-bold text-warning">{summary.totalCustomersWithBalance}</div>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-3">
          <div className="card shadow-sm border-start border-primary border-4">
            <div className="card-body">
              <div className="text-muted small">Total Agreed Fees</div>
              <div className="fs-3 fw-bold text-primary">
                ₹{Number(summary.totalFees).toFixed(2)}
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-3">
          <div className="card shadow-sm border-start border-success border-4">
            <div className="card-body">
              <div className="text-muted small">Total Collected</div>
              <div className="fs-3 fw-bold text-success">
                ₹{Number(summary.totalCollected).toFixed(2)}
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-3">
          <div className="card shadow-sm border-start border-danger border-4">
            <div className="card-body">
              <div className="text-muted small">Total Outstanding Balance</div>
              <div className="fs-3 fw-bold text-danger">
                ₹{Number(summary.totalOutstanding).toFixed(2)}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <div className="row g-3 align-items-center mb-3">
            <div className="col-12 col-md-6 col-lg-8">
              <SpeechInput
                as="input"
                name="searchTerm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by Customer Name, Mobile, Application No, Work Details..."
              />
            </div>
            <div className="col-12 col-md-6 col-lg-4 text-md-end">
              <span className="text-muted small">
                Showing {filteredItems.length} of {items.length} pending accounts
              </span>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Application No</th>
                  <th>Customer Name</th>
                  <th>Mobile</th>
                  <th>Work Details</th>
                  <th>Total Fee</th>
                  <th>Total Paid</th>
                  <th>Pending Balance</th>
                  <th>Status</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={9} className="text-center py-4 text-muted">
                      Calculating customer pending balances...
                    </td>
                  </tr>
                ) : null}

                {!loading && filteredItems.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-center py-4 text-muted">
                      🎉 No customers with pending balance found!
                    </td>
                  </tr>
                ) : null}

                {!loading &&
                  filteredItems.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <div className="fw-bold">{item.applicationNo}</div>
                        <div className="text-muted small">
                          {new Date(item.createdAt).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="fw-bold text-uppercase">{item.customerName}</td>
                      <td>
                        <a href={`tel:${item.customerMobile}`} className="text-decoration-none">
                          📱 {item.customerMobile}
                        </a>
                      </td>
                      <td>
                        <div className="fw-semibold small">{item.workTypeName || "-"}</div>
                        <div className="text-muted small">
                          {item.workDescription || item.workName || "-"}
                        </div>
                        {item.vehicleNo ? (
                          <div className="badge text-bg-light border text-dark mt-1">
                            🚗 {item.vehicleNo}
                          </div>
                        ) : null}
                      </td>
                      <td>₹{Number(item.totalWorkFee).toFixed(2)}</td>
                      <td className="text-success fw-semibold">
                        ₹{Number(item.totalPaid).toFixed(2)}
                      </td>
                      <td>
                        <span className="badge text-bg-danger fs-6">
                          ₹{Number(item.pendingBalance).toFixed(2)}
                        </span>
                      </td>
                      <td>
                        <span className="badge text-bg-warning">{item.status}</span>
                      </td>
                      <td className="text-end">
                        <div className="btn-group btn-group-sm">
                          <button
                            className="btn btn-success"
                            onClick={() => setSelectedEnquiryForPayment(item)}
                          >
                            💳 Collect Payment
                          </button>
                          <Link className="btn btn-outline-primary" to={`/admin/enquiries/${item.id}`}>
                            View
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {selectedEnquiryForPayment ? (
        <PaymentReceiptModal
          enquiry={selectedEnquiryForPayment}
          onClose={() => setSelectedEnquiryForPayment(null)}
          onPaymentRecorded={loadData}
        />
      ) : null}
    </div>
  );
}
